import React from 'react'
import { supabase } from '@/lib/supabase'
import { fetchProfile, ensureProfile } from '@/modules/profile/services/profileService'
import { normalizeRole } from '@/shared/utils/role'

export type Profile = {
  id: string
  full_name: string | null
  phone: string | null
  country?: string | null
  role: 'master' | 'member' | 'afiliado'
  must_change_password?: boolean
  avatar_url?: string | null
}

type AuthState = {
  user: import('@supabase/supabase-js').User | null
  profile: Profile | null
  loading: boolean
  signIn: (email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
  reloadProfile: () => Promise<void>
}

export const AuthContext = React.createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = React.useState<AuthState['user']>(null)
  const [profile, setProfile] = React.useState<Profile | null>(null)
  const [loading, setLoading] = React.useState(true)

  const buildFallbackProfile = React.useCallback((authUser: import('@supabase/supabase-js').User): Profile => {
    const meta = (authUser.user_metadata || {}) as Record<string, any>
    const rawRole = meta.role || authUser.app_metadata?.role || ''
    const normalized = normalizeRole(rawRole)
    const role = (normalized === 'master' || normalized === 'afiliado' || normalized === 'member')
      ? normalized
      : 'member'
    return {
      id: authUser.id,
      full_name: (meta.full_name || meta.name || null),
      phone: (meta.phone || null),
      country: (meta.country || null),
      role: role as Profile['role'],
      must_change_password: false,
      avatar_url: (meta.avatar_url || null),
    }
  }, [])

  const loadProfile = React.useCallback(async (authUser: AuthState['user']) => {
    if (!authUser?.id) {
      setProfile(null)
      return
    }
    try {
      await ensureProfile(authUser.id)
    } catch {}
    try {
      const prof = await fetchProfile('self')
      if (prof) {
        setProfile(prof as any)
        return
      }
    } catch {}
    setProfile(buildFallbackProfile(authUser))
  }, [buildFallbackProfile])

  React.useEffect(() => {
    let mounted = true
    ;(async () => {
      try {
        const { data, error } = await supabase.auth.getSession()
        if (!mounted) return

        if (error) {
          console.error('[Auth] getSession error:', error)
          setUser(null)
          setProfile(null)
        } else {
          const authUser = data.session?.user ?? null
          setUser(authUser)
          if (authUser) {
            // carrega o profile em background
            loadProfile(authUser)
          } else {
            setProfile(null)
          }
        }
      } catch (err) {
        console.error('[Auth] getSession exception:', err)
        if (mounted) {
          setUser(null)
          setProfile(null)
        }
      } finally {
        if (mounted) setLoading(false)
      }
    })()

    const { data: sub } = supabase.auth.onAuthStateChange(async (_event, session) => {
      const authUser = session?.user ?? null
      setUser(authUser)
      if (authUser) {
        loadProfile(authUser)
      } else {
        setProfile(null)
      }
    })

    return () => {
      mounted = false
      try {
        sub?.subscription?.unsubscribe?.()
      } catch {}
    }
  }, [loadProfile])

  const reloadProfile = React.useCallback(async () => {
    await loadProfile(user)
  }, [loadProfile, user?.id])

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) throw error
  }
  const signOut = async () => {
    try {
      await supabase.auth.signOut({ scope: 'local' } as any)
    } catch {}
    // Limpeza defensiva do estado local
    try {
      setUser(null)
      setProfile(null)
    } catch {}
    // Remoção defensiva de tokens locais (variam por projeto)
    try {
      const ref = (new URL((supabase as any)._url)).hostname.split('.')[0]
      Object.keys(localStorage).forEach((k) => { if (k.startsWith('sb-') || k.includes(ref)) localStorage.removeItem(k) })
    } catch {}
  }

  return (
    <AuthContext.Provider value={{ user, profile, loading, signIn, signOut, reloadProfile }}>{children}</AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = React.useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
